import { Field, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import {
  IAddCheckItemTemplate,
  ICheckItem,
} from '../../../models/forms/ICheckItemTemplate';
import { IReactSelectOption } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormTextArea from '../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import { CheckItemTemplateValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import {
  PlusIcon,
  UpIcon,
  DownIcon,
  RemoveIcon,
} from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';
import { injectIntl } from 'react-intl';

interface IProps extends IAddEditFormProps<IAddCheckItemTemplate> {
  allProcessOptions: IReactSelectOption[];
  isLoading: boolean;
}

const customStyles = {
  control: (base) => ({
    ...base,
    // This line disable the blue border
    boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    borderRadius: '8px',
    minHeight: '46px',
    border: '1px solid var(--bs-border-color)',
  }),

  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isFocused ? '#33439c' : isSelected ? '#33439c' : null,
    color: isFocused ? '#fff' : isSelected ? '#fff' : '#000',
  }),
};

interface IHeaderGroup {
  localId: string;
  Title: string;
  IsEditing: boolean;
  Items: ICheckItem[];
}

const AddEditCheckItemTemplateForm = injectIntl(
  (props: IProps & { intl: any }) => {
    const TEMPLATE_NAME_MAX_LENGTH = 256;
    const [headerGroups, setHeaderGroups] = useState<IHeaderGroup[]>([]);
    const [showError, setShowError] = useState(false);
    const [lastAddedRowKey, setLastAddedRowKey] = useState<string | null>(
      null
    );
    const formikRef = useRef<any>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const newRowRef = useRef<HTMLTableRowElement | null>(null);

    const HEADER_TITLE_MAX_LENGTH = 100;
    const CHECK_ITEM_MESSAGE_MAX_LENGTH = 250;

    const validateLengthLimits = () => {
      const headerTooLong = headerGroups.some(
        (g) => (g.Title || '').trim().length >= HEADER_TITLE_MAX_LENGTH
      );
      if (headerTooLong) {
        toast.error(formatMessage('max_char_limit_100'));
        return true;
      }

      const itemTooLong = headerGroups.some((g) =>
        g.Items.some(
          (item) =>
            (item.CheckItemMessage || '').trim().length >=
            CHECK_ITEM_MESSAGE_MAX_LENGTH
        )
      );
      if (itemTooLong) {
        toast.error(formatMessage('max_process_limit_reached'));
        return true;
      }

      return false;
    };

    useEffect(() => {
      const initialValues = props.initialValues as any;
      const rawHeaders = initialValues?.Headers || initialValues?.Header || [];
      if (rawHeaders.length > 0) {
        const initialGroups = rawHeaders.map((header: any) => ({
          localId: Math.random().toString(36).substr(2, 9),
          Title: header.Title || header.ChechItemHeader,
          IsEditing: false,
          Items: (header.CheckItems || header.CheckItemMessage).map(
            (item: any) => ({
              ...item,
              IsInfo: item.IsChecked, // Map IsChecked back to IsInfo for internal UI state
              IsTouched: false,
            })
          ),
        }));
        setHeaderGroups(initialGroups);
      } else if (props.initialValues?.CheckItemMessage) {
        const items = props.initialValues.CheckItemMessage.filter(
          (item) => (item.CheckItemMessage || '').trim() !== ''
        );

        if (items.length > 0) {
          // Group items by HeaderTitle
          const groups: { [key: string]: ICheckItem[] } = {};
          const groupOrder: string[] = [];

          items.forEach((item) => {
            const title = item.HeaderTitle || '';
            if (!groups[title]) {
              groups[title] = [];
              groupOrder.push(title);
            }
            groups[title].push(item);
          });

          const initialGroups = groupOrder.map((title) => ({
            localId: Math.random().toString(36).substr(2, 9),
            Title: title,
            IsEditing: false,
            Items: groups[title],
          }));
          setHeaderGroups(initialGroups);
        } else {
          setHeaderGroups([]);
        }
      }
    }, [
      props.initialValues?.CheckItemMessage,
      (props.initialValues as any)?.Headers,
      (props.initialValues as any)?.Header,
      props.id,
    ]);

    const addHeaderHandler = () => {
      const newHeader: IHeaderGroup = {
        localId: Math.random().toString(36).substr(2, 9),
        Title: '',
        IsEditing: true,
        Items: [],
      };
      setHeaderGroups((prev) => [...prev, newHeader]);
      setLastAddedRowKey(`header-${newHeader.localId}`);
    };

    const toggleHeaderEdit = (localId: string, isEditing: boolean) => {
      setHeaderGroups((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, IsEditing: isEditing } : g
        )
      );
    };

    const handleHeaderTitleChange = (localId: string, value: string) => {
      const trimmedValue = value.slice(0, HEADER_TITLE_MAX_LENGTH);
      setHeaderGroups((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, Title: trimmedValue } : g
        )
      );
    };

    const removeHeaderHandler = (localId: string) => {
      setHeaderGroups((prev) => prev.filter((g) => g.localId !== localId));
    };

    const moveHeaderUp = (index: number) => {
      if (index === 0) return;
      setHeaderGroups((prev) => {
        const newList = [...prev];
        [newList[index - 1], newList[index]] = [
          newList[index],
          newList[index - 1],
        ];
        return newList;
      });
    };

    const moveHeaderDown = (index: number) => {
      if (index === headerGroups.length - 1) return;
      setHeaderGroups((prev) => {
        const newList = [...prev];
        [newList[index + 1], newList[index]] = [
          newList[index],
          newList[index + 1],
        ];
        return newList;
      });
    };

    const addItemToHeader = (localId: string) => {
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === localId) {
            const touchedItems = g.Items.map((item) => ({
              ...item,
              IsTouched: true,
            }));
            setLastAddedRowKey(`item-${localId}-${touchedItems.length}`);
            return {
              ...g,
              Items: [
                ...touchedItems,
                {
                  Id: 0,
                  CheckItemMessage: '',
                  IsChecked: false,
                  IsInfo: false,
                  Order: 0, // Will be recalculated on save
                  IsTouched: false,
                  HeaderTitle: g.Title,
                },
              ],
            };
          }
          return g;
        })
      );
    };

    const handleItemChange = (
      headerLocalId: string,
      itemIndex: number,
      value: string
    ) => {
      const trimmedValue = value.slice(0, CHECK_ITEM_MESSAGE_MAX_LENGTH);
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === headerLocalId) {
            const newItems = [...g.Items];
            newItems[itemIndex].CheckItemMessage = trimmedValue;
            newItems[itemIndex].IsTouched = true;
            return { ...g, Items: newItems };
          }
          return g;
        })
      );
    };

    const handleItemIsInfoChange = (
      headerLocalId: string,
      itemIndex: number,
      value: boolean
    ) => {
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === headerLocalId) {
            const newItems = [...g.Items];
            newItems[itemIndex].IsInfo = value;
            return { ...g, Items: newItems };
          }
          return g;
        })
      );
    };

    const removeItemHandler = (headerLocalId: string, itemIndex: number) => {
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === headerLocalId) {
            return { ...g, Items: g.Items.filter((_, i) => i !== itemIndex) };
          }
          return g;
        })
      );
    };

    const moveItemUp = (headerLocalId: string, itemIndex: number) => {
      if (itemIndex === 0) return;
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === headerLocalId) {
            const newItems = [...g.Items];
            [newItems[itemIndex - 1], newItems[itemIndex]] = [
              newItems[itemIndex],
              newItems[itemIndex - 1],
            ];
            return { ...g, Items: newItems };
          }
          return g;
        })
      );
    };

    const moveItemDown = (headerLocalId: string, itemIndex: number) => {
      setHeaderGroups((prev) =>
        prev.map((g) => {
          if (g.localId === headerLocalId && itemIndex < g.Items.length - 1) {
            const newItems = [...g.Items];
            [newItems[itemIndex + 1], newItems[itemIndex]] = [
              newItems[itemIndex],
              newItems[itemIndex + 1],
            ];
            return { ...g, Items: newItems };
          }
          return g;
        })
      );
    };

    const normalizeHeaderTitle = (title: string) =>
      (title || '').trim().toLowerCase();

    const hasDuplicateHeaderTitles = () => {
      const headerTitles = headerGroups
        .map((group) => normalizeHeaderTitle(group.Title))
        .filter((title) => title !== '');
      return new Set(headerTitles).size !== headerTitles.length;
    };

    const isDuplicateHeaderTitle = (localId: string) => {
      const currentGroup = headerGroups.find(
        (group) => group.localId === localId
      );
      if (!currentGroup) return false;

      const currentTitle = normalizeHeaderTitle(currentGroup.Title);
      if (!currentTitle) return false;

      const matchCount = headerGroups.filter(
        (group) => normalizeHeaderTitle(group.Title) === currentTitle
      ).length;

      return matchCount > 1;
    };

    const validateDuplicateItemsWithinHeader = () => {
      let hasGlobalDuplicate = false;
      headerGroups.forEach((g) => {
        const messages = g.Items.map((i) =>
          i.CheckItemMessage.trim().toLowerCase()
        ).filter((m) => m !== '');
        const uniqueMessages = new Set(messages);
        if (uniqueMessages.size !== messages.length) {
          hasGlobalDuplicate = true;
        }
      });
      return hasGlobalDuplicate;
    };

    const hasHeaderWithoutCheckItem = () => {
      return headerGroups.some((group) => {
        const validItemCount = group.Items.filter(
          (item) => (item.CheckItemMessage || '').trim() !== ''
        ).length;
        return validItemCount === 0;
      });
    };

    const hasNoHeaders = () => headerGroups.length === 0;

    const hasHeaderWithoutTitle = () => {
      return headerGroups.some((group) => (group.Title || '').trim() === '');
    };

    useEffect(() => {
      if (!lastAddedRowKey) return;
      if (newRowRef.current) {
        newRowRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      setLastAddedRowKey(null);
    }, [lastAddedRowKey, headerGroups]);

    return (
      <Modal
        show={true}
        centered
        onHide={props.onModalClosed}
        size="xl"
        className="template-modal"
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="add_template" />
            ) : (
              <FormattedMessage id="update_template" />
            )}
            <button
              onClick={props.onModalClosed}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>

        <Formik
          innerRef={formikRef}
          initialValues={props.initialValues}
          enableReinitialize
          validationSchema={CheckItemTemplateValidateSchema}
          onSubmit={(values) => {
            if (validateLengthLimits()) {
              return;
            }

            if (hasNoHeaders()) {
              setShowError(true);
              toast.error(formatMessage('please_add_header_before_save'));
              return;
            }

            if (hasHeaderWithoutTitle()) {
              // setShowError(true);
              toast.error(formatMessage('please_enter_header_title'));
              return;
            }

            setShowError(false);

            if (hasHeaderWithoutCheckItem()) {
              setHeaderGroups((prev) =>
                prev.map((group) => ({
                  ...group,
                  Items: group.Items.map((item) => ({
                    ...item,
                    IsTouched: true,
                  })),
                }))
              );
              toast.error(formatMessage('header_requires_check_item'));
              return;
            }

            const hasEmpty = headerGroups.some((g) =>
              g.Items.some(
                (item) => (item.CheckItemMessage || '').trim() === ''
              )
            );

            if (hasEmpty) {
              setHeaderGroups((prev) =>
                prev.map((g) => ({
                  ...g,
                  Items: g.Items.map((item) => ({ ...item, IsTouched: true })),
                }))
              );
              return;
            }

            if (hasDuplicateHeaderTitles()) {
              toast.error(formatMessage('duplicate_header_names_not_allowed'));
              return;
            }

            if (validateDuplicateItemsWithinHeader()) {
              toast.error(formatMessage('duplicate_item_in_header'));
              return;
            }

            const selectedProcess = props.allProcessOptions.find(
              (o) => Number(o.value) === values.ProcessId
            );

            const finalPayload: IAddCheckItemTemplate = {
              TenantId: Number(getTenantIdFromLocalStorage()),
              CheckItemName: values.CheckItemName,
              ProcessId: values.ProcessId,
              ProcessName: selectedProcess ? selectedProcess.label : '',
              Remarks: values.Remarks,
              Headers: headerGroups.map((g, gIdx) => ({
                Title: g.Title,
                Order: gIdx + 1,
                CheckItems: g.Items.map((item, iIdx) => ({
                  Id: item.Id,
                  CheckItemMessage: item.CheckItemMessage,
                  Order: iIdx + 1,
                  IsChecked: !!item.IsInfo,
                })),
              })),
            };

            if (values.Id > 0) {
              finalPayload.Id = values.Id;
            }

            // console.log('Final Payload on Save/Update:', finalPayload);
            props.submitHandler(finalPayload);
          }}
        >
          {({
            setFieldValue,
            values,
            errors,
            touched,
            setFieldTouched,
            submitCount,
          }) => {
            let globalCounter = 0;
            return (
              <Form className="user-form check-template-modal">
                {props.isLoading && <FormLoader loading={props.isLoading} />}
                <Modal.Body>
                  <div className="row g-4">
                    <div className="col-lg-5">
                      <div className="form-group mb-3">
                        <Label
                          required
                          label="template_name"
                          className="label-style col-form-label"
                        />
                        <Field
                          className="form-control"
                          name="CheckItemName"
                          type="text"
                          as={FormikFormInput}
                          maxLength={TEMPLATE_NAME_MAX_LENGTH}
                          disabled={props.id > 0}
                        />
                        {String(values.CheckItemName || '').length >=
                          TEMPLATE_NAME_MAX_LENGTH && (
                          <div className="invalid-feedback d-block errorMessage">
                           {formatMessage('max_char_limit_256')}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-0">
                        <Label
                          className="label-style col-form-label"
                          required={false}
                          label="remarks"
                        />
                        <Field
                          id="Remarks"
                          className="form-control process-textarea"
                          name="Remarks"
                          placeholder=""
                          type="text"
                          as={FormikFormTextArea}
                        />
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="form-group mb-3">
                        <label className="label-style col-form-label">
                          <FormattedMessage id="select_process" />
                        </label>

                        <Select
                          styles={customStyles}
                          className="select-did-process"
                          classNames={{
                            option: (d) =>
                              `our-option ${d.isSelected ? 'active' : ''}`,
                          }}
                          options={props.allProcessOptions}
                          value={props.allProcessOptions.find(
                            (o) => Number(o.value) === values.ProcessId
                          )}
                          onChange={(opt: any) => {
                            setFieldValue('ProcessId', Number(opt.value));
                          }}
                          placeholder={formatMessage('please_select')}
                        />
                        {errors.ProcessId && submitCount > 0 && (
                          <div className="text-danger errorMessage mt-1 small">
                            <FormattedMessage id={errors.ProcessId as string} />
                          </div>
                        )}
                      </div>

                      <div className="tableDesign row-eq-height tableDevices changingPanel processTable check-item-list border rounded bg-white mt-4">
                        <div
                          className="table-responsive"
                          ref={scrollRef}
                        >
                          <table className="table mb-0">
                            <thead className="sticky-top bg-white">
                              <tr>
                                <th>{formatMessage('no')}</th>
                                <th>{formatMessage('check_item_content')}</th>
                                <th>{formatMessage('is_info')}</th>
                                <th>
                                  <div className="d-flex justify-content-end">
                                    <button
                                      type="button"
                                      className="btn btn-secondary add-header-btn"
                                      onClick={addHeaderHandler}
                                    >
                                      <PlusIcon />{' '}
                                      <span className="ps-1">
                                        {formatMessage('add_header')}
                                      </span>
                                    </button>
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {headerGroups.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="text-center p-5 text-muted"
                                  >
                                    {formatMessage('no_headers_added')}
                                  </td>
                                </tr>
                              ) : (
                                headerGroups.map((group, gIndex) => {
                                  const messages = group.Items.map((i) =>
                                    i.CheckItemMessage.trim().toLowerCase()
                                  ).filter((m) => m !== '');
                                  const duplicateItemInThisHeader =
                                    messages.length !== new Set(messages).size;
                                  const duplicateHeaderTitle =
                                    isDuplicateHeaderTitle(group.localId);

                                  return (
                                    <React.Fragment key={group.localId}>
                                      {/* Header Row */}
                                      <tr
                                        className="check-item-header bg-light"
                                        ref={
                                          lastAddedRowKey ===
                                          `header-${group.localId}`
                                            ? newRowRef
                                            : undefined
                                        }
                                      >
                                        <td colSpan={3}>
                                          <div className="header-title-input">
                                            {group.IsEditing ? (
                                              <>
                                                <input
                                                  autoFocus
                                                  type="text"
                                                  className={`form-control header-input ${group.Title.trim().length >= HEADER_TITLE_MAX_LENGTH || duplicateHeaderTitle || (showError && group.Title.trim() === '') ? 'is-invalid' : ''}`}
                                                  value={group.Title}
                                                  onChange={(e) =>
                                                    handleHeaderTitleChange(
                                                      group.localId,
                                                      e.target.value
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    toggleHeaderEdit(
                                                      group.localId,
                                                      false
                                                    )
                                                  }
                                                  onKeyDown={(e) =>
                                                    e.key === 'Enter' &&
                                                    toggleHeaderEdit(
                                                      group.localId,
                                                      false
                                                    )
                                                  }
                                                  maxLength={
                                                    HEADER_TITLE_MAX_LENGTH
                                                  }
                                                />
                                                {showError &&
                                                  group.Title.trim() === '' && (
                                                    <div className="invalid-feedback d-block errorMessage">
                                                      {formatMessage(
                                                        'please_enter_header_title'
                                                      )}
                                                    </div>
                                                  )}
                                                {duplicateHeaderTitle && (
                                                  <div className="invalid-feedback d-block errorMessage">
                                                    {formatMessage(
                                                      'duplicate_header_names_not_allowed'
                                                    )}
                                                  </div>
                                                )}
                                                {group.Title.trim().length >=
                                                  HEADER_TITLE_MAX_LENGTH && (
                                                  <div className="invalid-feedback d-block errorMessage">
                                                    {formatMessage(
                                                      'max_char_limit_100'
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <>
                                                <div
                                                  className={`header-title-text show-Elipses ${group.Title.trim().length >= HEADER_TITLE_MAX_LENGTH || duplicateHeaderTitle || (showError && group.Title.trim() === '') ? 'is-invalid' : ''}`}
                                                  onClick={() =>
                                                    toggleHeaderEdit(
                                                      group.localId,
                                                      true
                                                    )
                                                  }
                                                >
                                                  {group.Title || (
                                                    <span className="text-muted italic">
                                                      {formatMessage(
                                                        'header_name_placeholder'
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                                {showError &&
                                                  group.Title.trim() === '' && (
                                                    <div className="invalid-feedback d-block errorMessage">
                                                      {formatMessage(
                                                        'please_enter_header_title'
                                                      )}
                                                    </div>
                                                  )}
                                                {duplicateHeaderTitle && (
                                                  <div className="invalid-feedback d-block errorMessage">
                                                    {formatMessage(
                                                      'duplicate_header_names_not_allowed'
                                                    )}
                                                  </div>
                                                )}
                                                {group.Title.trim().length >=
                                                  HEADER_TITLE_MAX_LENGTH && (
                                                  <div className="invalid-feedback d-block errorMessage">
                                                    {formatMessage(
                                                      'max_char_limit_100'
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="d-flex justify-content-end action-buttons-group">
                                            <button
                                              type="button"
                                              className="btn btn-icon  btn-primary btn-sm"
                                              onClick={() =>
                                                addItemToHeader(group.localId)
                                              }
                                              title={formatMessage('add_item')}
                                            >
                                              <PlusIcon />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-light-primary btn-sm"
                                              onClick={() =>
                                                moveHeaderUp(gIndex)
                                              }
                                              disabled={gIndex === 0}
                                            >
                                              <UpIcon />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-light-primary  btn-sm"
                                              onClick={() =>
                                                moveHeaderDown(gIndex)
                                              }
                                              disabled={
                                                gIndex ===
                                                headerGroups.length - 1
                                              }
                                            >
                                              <DownIcon />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn-icon btn-light-danger  btn-sm cross-header"
                                              onClick={() =>
                                                removeHeaderHandler(
                                                  group.localId
                                                )
                                              }
                                            >
                                              <RemoveIcon />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                      {/* Item Rows */}
                                      {group.Items.map((item, iIndex) => {
                                        globalCounter++;
                                        const isDuplicate =
                                          group.Items.filter(
                                            (i) =>
                                              i.CheckItemMessage.trim().toLowerCase() ===
                                                item.CheckItemMessage.trim().toLowerCase() &&
                                              i.CheckItemMessage.trim() !== ''
                                          ).length > 1;
                                        const isEmptyTouched =
                                          item.IsTouched &&
                                          (
                                            item.CheckItemMessage || ''
                                          ).trim() === '';

                                        return (
                                          <tr
                                            key={iIndex}
                                            ref={
                                              lastAddedRowKey ===
                                              `item-${group.localId}-${iIndex}`
                                                ? newRowRef
                                                : undefined
                                            }
                                          >
                                            <td className="text-center align-middle">
                                              {globalCounter}
                                            </td>
                                            <td>
                                              <div className="position-relative">
                                                <>
                                                  <input
                                                    type="text"
                                                    className={`form-control ${(item.IsTouched && (item.CheckItemMessage || '').trim() === '') || isDuplicate || (item.CheckItemMessage || '').trim().length >= CHECK_ITEM_MESSAGE_MAX_LENGTH ? 'is-invalid' : ''}`}
                                                    value={
                                                      item.CheckItemMessage
                                                    }
                                                    onChange={(e) =>
                                                      handleItemChange(
                                                        group.localId,
                                                        iIndex,
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder={formatMessage(
                                                      'enter_check_content'
                                                    )}
                                                    onBlur={() => {
                                                      setHeaderGroups((prev) =>
                                                        prev.map((g) => {
                                                          if (
                                                            g.localId ===
                                                            group.localId
                                                          ) {
                                                            const newItems = [
                                                              ...g.Items,
                                                            ];
                                                            newItems[
                                                              iIndex
                                                            ].IsTouched = true;
                                                            return {
                                                              ...g,
                                                              Items: newItems,
                                                            };
                                                          }
                                                          return g;
                                                        })
                                                      );
                                                    }}
                                                    maxLength={
                                                      CHECK_ITEM_MESSAGE_MAX_LENGTH
                                                    }
                                                  />
                                                  {isEmptyTouched && (
                                                    <div className="invalid-feedback d-block">
                                                      {formatMessage(
                                                        'please_enter_check_item_content'
                                                      )}
                                                    </div>
                                                  )}
                                                  {(isDuplicate ||
                                                    duplicateItemInThisHeader) && (
                                                    <div className="invalid-feedback">
                                                      {formatMessage(
                                                        'duplicate_item_in_header'
                                                      )}
                                                    </div>
                                                  )}
                                                  {(
                                                    item.CheckItemMessage || ''
                                                  ).trim().length >=
                                                    CHECK_ITEM_MESSAGE_MAX_LENGTH && (
                                                    <div className="invalid-feedback d-block">
                                                      {formatMessage(
                                                        'max_process_limit_reached'
                                                      )}
                                                    </div>
                                                  )}
                                                </>
                                              </div>
                                            </td>
                                            <td className="text-center align-middle">
                                              <div className="form-check d-flex justify-content-center m-0">
                                                <input
                                                  type="checkbox"
                                                  className="form-check-input"
                                                  checked={!!item.IsInfo}
                                                  onChange={(e) =>
                                                    handleItemIsInfoChange(
                                                      group.localId,
                                                      iIndex,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              </div>
                                            </td>
                                            <td>
                                              <div className="d-flex justify-content-end action-buttons-group">
                                                <button
                                                  type="button"
                                                  className="btn btn-icon btn-light-primary btn-sm"
                                                  onClick={() =>
                                                    moveItemUp(
                                                      group.localId,
                                                      iIndex
                                                    )
                                                  }
                                                  disabled={iIndex === 0}
                                                >
                                                  <UpIcon />
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-icon btn-light-primary btn-sm"
                                                  onClick={() =>
                                                    moveItemDown(
                                                      group.localId,
                                                      iIndex
                                                    )
                                                  }
                                                  disabled={
                                                    iIndex ===
                                                    group.Items.length - 1
                                                  }
                                                >
                                                  <DownIcon />
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-icon btn-light-danger btn-sm"
                                                  onClick={() =>
                                                    removeItemHandler(
                                                      group.localId,
                                                      iIndex
                                                    )
                                                  }
                                                >
                                                  <RemoveIcon />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <div className="d-flex justify-content-end w-100">
                    <Button
                      variant="light-danger"
                      onClick={props.onModalClosed}
                      className="me-2"
                    >
                      {formatMessage('cancel')}
                    </Button>
                    <LoaderButton
                      type="submit"
                      isLoading={props.isLoading}
                      text="button_save"
                    />
                  </div>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    );
  }
);

export default AddEditCheckItemTemplateForm;
